import React, { useEffect } from 'react'
import { kbControlsLayerDefault, useKeyboardControls } from '../../../contexts/KeyboardControlsContext'

interface KeyboardControlProps {
  children: React.ReactElement

  /**
   * The KB controls layer the element belongs to. The active layer can be switched with the `setActiveLayer` function from the `KeyboardControlsContext`.
   */
  layer?: string

  /**
   * Specifies if the element is externally controlled, such as an iframe or Unity component.
   *
   * @default false
   */
  isExternal?: boolean
  /**
   * If true, the element is responsible for giving away focus when appropriate.
   *
   * @default false
   */
  manualFocusHandling?: boolean

  onFocusEnterFromStart?: (() => void) | null
  onFocusEnterFromEnd?: (() => void) | null

  /**
   * If true, the wrapped element is (or stays) unregistered from the Tab order, so Tab/Shift-Tab
   * never stops on it and it never reports itself as focused - without unmounting it, unlike
   * conditionally omitting this wrapper. Toggling this prop over time (e.g. from a `disabled` button
   * state) registers/unregisters the element as it flips. Other callers are unaffected by this prop
   * existing, since it defaults to false (today's behavior: always registered while mounted).
   *
   * @default false
   */
  disabled?: boolean
};

/**
 * A wrapper component that adds keyboard control functionality to the provided child element.
 *
 * Requires the `KeyboardControlContext` to be provided by `KeyboardControlsProvider`.
 *
 * @param {string} [props.layer] - The KB controls layer the element belongs to. The active layer can be switched with the `setActiveLayer` function from the `KeyboardControlsContext`.
 * @param {boolean} [props.isExternal=false] - Whether the child element is externally managed (e.g., an iframe or Unity content).
 * @param {boolean} [props.manualFocusHandling=false] - If true, the element is responsible for giving away focus when appropriate.
 * @param {boolean} [props.disabled=false] - If true, keeps the element out of the Tab order without unmounting it.
 */
const KeyboardControl: React.FC<KeyboardControlProps> = ({ children, disabled = false, isExternal = false, layer = kbControlsLayerDefault, manualFocusHandling = false, onFocusEnterFromStart = null, onFocusEnterFromEnd = null }: KeyboardControlProps) => {
  const keyboardControls = useKeyboardControls()

  const extendedChild = React.cloneElement(children, {
    onFocus: keyboardControls.handleNonKeyboardFocus
  })

  const id = extendedChild.props.id

  useEffect(() => {
    if (disabled) return

    keyboardControls.addFocusableElement(id, layer, isExternal, manualFocusHandling, onFocusEnterFromStart, onFocusEnterFromEnd)

    return () => {
      keyboardControls.removeFocusableElement(id, layer)
    }
  }, [keyboardControls, id, isExternal, manualFocusHandling, onFocusEnterFromStart, onFocusEnterFromEnd, layer, disabled])

  return (
    extendedChild
  )
}

export default KeyboardControl
